import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLicenseStore } from '@/stores/license'
import { showExpiryLockout, systemDataActive } from '@/lib/licenseGate'
import { mselDoctorRoutes } from '@/modules/msel-doctor/routes'
import { metlSmithRoutes } from '@/modules/metl-smith/routes'
import { baronRoutes } from '@/modules/baron/routes'
import { scribeRoutes } from '@/modules/scribe/routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: (_to) => {
        const authStore = useAuthStore()
        return authStore.isAuthenticated ? '/scenarios' : '/login'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/license-expired',
      name: 'license-expired',
      component: () => import('@/views/LicenseExpiredView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/license',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'license',
          component: () => import('@/views/LicenseView.vue')
        }
      ]
    },
    ...mselDoctorRoutes,
    ...metlSmithRoutes,
    ...baronRoutes,
    ...scribeRoutes,
    {
      path: '/users',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true, requiresRole: 'admin' },
      children: [
        {
          path: '',
          name: 'users',
          component: () => import('@/views/UsersView.vue')
        }
      ]
    },
    {
      path: '/feedback',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true, requiresErudioAdmin: true },
      children: [
        {
          path: '',
          name: 'feedback-reports',
          component: () => import('@/views/FeedbackReportsView.vue'),
        },
        {
          path: ':reportId',
          name: 'feedback-report',
          component: () => import('@/views/FeedbackReportsView.vue'),
        },
      ],
    },
    {
      path: '/version',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'version',
          component: () => import('@/views/VersionInfoView.vue')
        }
      ]
    },
    {
      path: '/system',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true, systemData: true },
      children: [
        {
          path: '',
          name: 'system',
          component: () => import('@/views/SystemDataManagement.vue')
        }
      ]
    },
    // 404 catch-all route - must be last
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue')
    }
  ],
})

// Navigation guards
function firstOpenModule(authStore: ReturnType<typeof useAuthStore>, licenseStore: ReturnType<typeof useLicenseStore>) {
  const modules: Array<[string, string]> = [
    ['msel-doctor', 'scenarios'],
    ['metl-smith', 'tasks-measures'],
    ['baron', 'baron-sessions'],
    ['scribe', 'scribe-queue'],
  ]
  for (const [moduleName, routeName] of modules) {
    if (authStore.hasModuleAccess(moduleName) && licenseStore.moduleAllowed(moduleName)) {
      return { name: routeName }
    }
  }
  return { name: 'version' }
}

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const licenseStore = useLicenseStore()

  // Always check authentication first, except for login page and 404
  if (to.name !== 'login' && to.name !== 'not-found') {
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }
    try {
      await licenseStore.ensureLoaded()
    } catch {
      if (!authStore.isAuthenticated) {
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
    }
    if (showExpiryLockout(licenseStore.state) && to.name !== 'license-expired') {
      next({ name: 'license-expired' })
      return
    }
    if (to.name === 'license-expired' && !showExpiryLockout(licenseStore.state)) {
      next(firstOpenModule(authStore, licenseStore))
      return
    }
    if (to.name === 'license' && !licenseStore.state?.viewer_is_erudio_admin) {
      next(firstOpenModule(authStore, licenseStore))
      return
    }
    if (
      to.matched.some((record) => record.meta.requiresErudioAdmin) &&
      !licenseStore.state?.viewer_is_erudio_admin
    ) {
      next(firstOpenModule(authStore, licenseStore))
      return
    }
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Check if route requires specific role
    const requiredRole = to.matched.find(record => record.meta.requiresRole)?.meta.requiresRole
    if (requiredRole && !authStore.hasRole(requiredRole as string)) {
      next({ name: 'scenarios' })
      return
    }

    const needsSystemData = to.matched.some(record => record.meta.systemData)
    if (needsSystemData) {
      const erudio = Boolean(licenseStore.state?.viewer_is_erudio_admin)
      const allowed = erudio || (authStore.hasRole('admin') && systemDataActive(licenseStore.state))
      if (!allowed) {
        next(firstOpenModule(authStore, licenseStore))
        return
      }
    }

    const routeModule = to.matched.find(record => record.meta.module)?.meta.module as string | undefined
    if (routeModule && (!authStore.hasModuleAccess(routeModule) || !licenseStore.moduleAllowed(routeModule))) {
      next(firstOpenModule(authStore, licenseStore))
      return
    }
  }

  // Redirect authenticated users away from login page
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (authStore.isAuthenticated) {
      const redirect = to.query.redirect as string
      next(redirect || { name: 'scenarios' })
      return
    }
  }

  next()
})

export default router
